import Link from 'next/link';
import type { FC } from 'react';
import { ReactNode, memo } from 'react';

import { StoryblokRichtext, render } from 'storyblok-rich-text-react-renderer';

import { StoryType } from '@virginmediao2/storyblok-sdk';

import { replaceSbRoot } from '@/environment/storyblok-root';
import { StoryblokComponentProps } from '@/storyblok/engine';

interface RichTextProps {
  document: StoryblokRichtext;
  StoryblokComponent: FC<StoryblokComponentProps>;
  storyblokRoot?: string;
}

/**
 * Reduce attributes, overlaying story.url onto href.
 * @FIXES VK-534 - Storyblok bug where link URL is not updated when story is moved.
 */
export const mapAttributes = (attrs: Record<string, unknown>) =>
  Object.entries(attrs)
    .filter(([key]) => key !== 'linktype')
    .reduce((acc, [key, value]) => {
      if (key === 'story') {
        const story = value as StoryType;

        if (!story?.full_slug) {
          return acc;
        }

        const strippedUrl = replaceSbRoot(story.full_slug);

        return { ...acc, href: `${strippedUrl}` };
      } else if (key === 'href') {
        const href = value as string;

        if ('href' in acc) {
          return acc;
        }

        return { ...acc, href: encodeURI(href) };
      }

      return { ...acc, [key]: value };
    }, {});

const renderOptions = (StoryblokComponent: FC<StoryblokComponentProps>) => ({
  // Handle rendering of component bloks
  defaultBlokResolver: (
    name: string,
    props: Record<string, unknown> & { _uid: string }
  ) => {
    const blok = { ...props, component: name };
    return <StoryblokComponent blok={blok} key={props._uid} />;
  },
  markResolvers: {
    // Provide custom resolver to handle story links
    link: (children: ReactNode, attrs: Record<string, unknown>) => {
      const href = 'href' in attrs ? encodeURI(attrs.href as string) : '';

      return (
        <Link href={href} prefetch={false} {...mapAttributes(attrs)}>
          {children}
        </Link>
      );
    },
  },
});

export const RenderRichText = memo<RichTextProps>(
  ({ document, StoryblokComponent }) => {
    return render(document, renderOptions(StoryblokComponent));
  }
);

RenderRichText.displayName = 'RenderRichText';
